// Making an app for the M5Stack Tab5 by talking to an agent: what the room,
// the server and the tests agree on (docs/make.md). Nothing here imports
// three.js or the DOM.

/**
 * Whether the Studio offers making in the room. `off`: the routes answer
 * 404 and no page shows it. `preview`: the routes answer, and the room shows
 * it only in a browser that opened the preview (`MAKE_PREVIEW_KEY`). `on`:
 * the room shows it to everyone.
 */
export type MakeMode = "off" | "preview" | "on";

export const parseMakeMode = (value: unknown): MakeMode => (value === "preview" || value === "on" ? value : "off");

/** The browser's switch for the preview: `/studio/?make=1` sets it, `?make=off` clears it. */
export const MAKE_PREVIEW_KEY = "pocket.make";

/**
 * Who may make while the maker is in preview (`MAKE_ALLOW`): addresses and
 * `@domain`s, separated by commas, lowercased. Empty: anyone signed in.
 */
export const parseMakeAllow = (value: unknown): string[] =>
  typeof value === "string" ? value.split(",").map((entry) => entry.trim().toLowerCase()).filter(Boolean) : [];

/** Whether a verified address is on the list: named whole, or ending in `@domain` exactly. */
export const makeAllows = (allow: readonly string[], email: string): boolean => {
  const address = email.trim().toLowerCase();
  return allow.some((entry) => (entry.startsWith("@") ? address.endsWith(entry) : address === entry));
};

/**
 * What each thing costs, in credits, and what an account is given. A price
 * is fixed per action so a visitor can tell before pressing, and is taken
 * when the action starts: a turn that fails gives it back. One credit
 * stands for about two US cents of model time.
 */
export const CREDITS = {
  /** A new app's first message, until the app has a version. */
  game: 20,
  /** Each message to an app that has a version. */
  change: 5,
  /** Given once, when an account first opens the maker. */
  trial: 60,
  /** A member's allowance, refilled to this at the start of each month of the membership. It does not roll over. */
  monthly: 300,
} as const;

/** The longest message a visitor may send. */
export const MESSAGE_MAX = 2000;

/** An account's credits, as the room shows them. */
export interface CreditsInfo {
  /** What can be spent now: the month's allowance first, then `extra`. */
  balance: number;
  /** What is left of this month's allowance; 0 for an account that is not a member. */
  monthly: number;
  /** Credits that do not run out: the first gift, and any an editor granted. */
  extra: number;
  /** When the month's allowance is refilled, epoch ms; null for an account that is not a member. */
  renews: number | null;
  member: boolean;
}

/** GET /api/make: whether the maker is open to this visitor, and what they can spend. */
export interface MakeInfo {
  mode: MakeMode;
  /** Whether apps for the M5Stack Tab5 can be made here: the Tab5 maker is set up. */
  tab5: boolean;
  /** Null for a guest, who signs in to make. */
  credits: CreditsInfo | null;
  prices: { game: number; change: number; trial: number; monthly: number };
}

/**
 * One version of a Tab5 app the agent saved, as its author sees it: its
 * `.pocket`, which runs in the player and is the file a Tab5 installs.
 */
export interface Tab5VersionInfo {
  rev: number;
  title: string;
  at: number;
  /** Where it runs: the Tab5 maker's player, an address only its author is given. */
  play: string;
  /** Where its author downloads its `.pocket`. */
  download: string;
  bytes: number;
  sha256: string;
}

/**
 * GET /api/make/tab5/:id: one of the account's Tab5 apps
 * (docs/make.md#the-tab5). An agent writes it in a container of its own;
 * the conversation is read from `/api/make/tab5/:id/conversation`.
 */
export interface Tab5GameInfo {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  /** Oldest first. */
  versions: Tab5VersionInfo[];
  /** The agent's last build after the newest version, which it is trying out: it plays like a version and is not one yet. */
  draft: { title: string; at: number; play: string; download: string } | null;
  /** A message's turn is running. */
  busy: boolean;
}

/** POST /api/make/tab5 and POST /api/make/tab5/:id/messages: the turn the message started. */
export interface Tab5TurnInfo {
  game: Tab5GameInfo;
  /** What names the turn in the conversation's settlements. */
  submissionId: string;
  credits: CreditsInfo;
}

/**
 * GET /api/make/tab5/:id/conversation: the conversation as the Tab5 maker's
 * Flue agent keeps it, as far as the room reads it. Each response of the
 * agent is one assistant message whose parts come in order: its words, each
 * tool it ran, and a `data-version` part for each version it saved.
 */
export interface Tab5Conversation {
  messages: Tab5Message[];
  settlements: Array<{ submissionId: string; outcome: "completed" | "failed" | "aborted" }>;
}

export interface Tab5Message {
  id: string;
  role: "user" | "assistant" | "system";
  display: "visible" | "hidden" | "diagnostic";
  /** Set on the note the agent's runtime adds when a turn failed or was stopped. */
  settlement?: { outcome: "failed" | "aborted" };
  parts: Tab5Part[];
}

export type Tab5Part =
  | { type: "text" | "reasoning"; text: string; state: "streaming" | "done" }
  | { type: "dynamic-tool"; toolName: string; toolCallId: string; state: "input-available" | "output-available" | "output-error"; input: unknown }
  | { type: "data-version"; data: { rev: number; title: string } }
  | { type: "data-draft"; data: { title: string } }
  | { type: string };

/** What the player's frame tells the page: that it is ready, or an error the app hit. */
export type FrameMessage =
  | { pocket: "error"; message: string; line?: number }
  | { pocket: "ready" };

/** The cost of the next message for an app that has a version or has none. */
export const turnPrice = (hasVersion: boolean): number => (hasVersion ? CREDITS.change : CREDITS.game);

/** A message from a visitor, cleaned: trimmed, at most MESSAGE_MAX characters. Null when empty. */
export function cleanMessage(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.replace(/\r\n?/g, "\n").trim();
  if (!text) return null;
  return text.length > MESSAGE_MAX ? text.slice(0, MESSAGE_MAX) : text;
}
